import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AdminContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL + '/admin';

export function AdminProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('nao_admin_token'));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  // Set axios default header for admin
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  const hasPermission = useCallback((permKey) => {
    return permissions.includes(permKey);
  }, [permissions]);

  const fetchAdminInfo = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(res.data.admin);
      setPermissions(res.data.admin.permissions || []);
    } catch (e) {
      console.error('Failed to fetch admin info:', e);
      // Token invalid, clear it
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      const { token: newToken, admin: adminData } = res.data;
      
      setToken(newToken);
      setAdmin(adminData);
      setPermissions(adminData.permissions || []);
      localStorage.setItem('nao_admin_token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      return { success: true };
    } catch (e) {
      console.error('Admin login failed:', e);
      return { success: false, error: e.response?.data?.error || 'Login failed' };
    }
  };

  const logout = () => {
    setToken(null);
    setAdmin(null);
    setPermissions([]);
    localStorage.removeItem('nao_admin_token');
    delete axios.defaults.headers.common['Authorization'];
  };

  useEffect(() => {
    fetchAdminInfo();
  }, [fetchAdminInfo]);

  return (
    <AdminContext.Provider value={{
      admin,
      token,
      loading,
      permissions,
      login,
      logout,
      hasPermission,
      isAuthenticated: !!token && !!admin
    }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
}
